import { useEffect, useState } from "react";
import axios from "axios";
import Seo from "@/components/seo/Seo";
import HeroBlock from "@/components/blocks/renderers/HeroBlock";
import HeroSplitBlock from "@/components/blocks/renderers/HeroSplitBlock";
import TwoColumnBlock from "@/components/blocks/renderers/TwoColumnBlock";
import CtaBannerBlock from "@/components/blocks/renderers/CtaBannerBlock";
import TextBlock from "@/components/blocks/renderers/TextBlock";
import ImageBlock from "@/components/blocks/renderers/ImageBlock";
import FeaturesGridBlock from "@/components/blocks/renderers/FeaturesGridBlock";
import HeroOverlayImageBlock from "@/components/blocks/renderers/HeroOverlayImageBlock";
import TestimonialsRotatorBlock from "@/components/blocks/renderers/TestimonialsRotatorBlock";
import WorkflowEngineBlock from "@/components/blocks/renderers/WorkflowEngineBlock";
import PartnerLogosBlock from "@/components/blocks/renderers/PartnerLogosBlock";
import SuccessStoryBlock from "@/components/blocks/renderers/SuccessStoryBlock";
import WhyAnteraBlock from "@/components/blocks/renderers/WhyAnteraBlock";
import SolutionDetailPageBlock from "@/components/blocks/renderers/SolutionDetailPageBlock";
import { UserGroupsPageBlock, ApiDocsPageBlock, SolutionsPageBlock, ProductsPageBlock, ExchangePageBlock, EliteSupplierPageBlock, IntegrationsPageBlock, AcademyPageBlock, EventsPageBlock, OpenHousePageBlock, CustomerPortalPageBlock } from "@/components/blocks/renderers/ContentPageBlocks";
import {
  SolutionsOverviewBlock,
  PowerEveryPartBlock,
  ProductsShowcaseBlock,
  HomeFeaturesGridBlock,
  GetStartedNowBlock,
  FinalCtaBlock,
  HomeCtaDarkBlock,
  HomeCtaLightBlock,
  ContactFormBlock,
} from "@/components/blocks/renderers/HomeWidgetBlocks";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Maps block type strings to their renderer component. Adding a new block:
//   1. Register the schema in registry.js
//   2. Create a renderer in ./renderers/<name>.jsx
//   3. Import + register it here.
const RENDERERS = {
  hero: HeroBlock,
  hero_split: HeroSplitBlock,
  two_column: TwoColumnBlock,
  cta_banner: CtaBannerBlock,
  text: TextBlock,
  image: ImageBlock,
  features_grid: FeaturesGridBlock,
  hero_overlay_image: HeroOverlayImageBlock,
  testimonials_rotator: TestimonialsRotatorBlock,
  workflow_engine: WorkflowEngineBlock,
  partner_logos: PartnerLogosBlock,
  solutions_overview: SolutionsOverviewBlock,
  power_every_part: PowerEveryPartBlock,
  why_antera: WhyAnteraBlock,
  products_showcase: ProductsShowcaseBlock,
  home_features_grid: HomeFeaturesGridBlock,
  success_story: SuccessStoryBlock,
  get_started_now: GetStartedNowBlock,
  final_cta: FinalCtaBlock,
  home_cta_dark: HomeCtaDarkBlock,
  home_cta_light: HomeCtaLightBlock,
  contact_form: ContactFormBlock,
  solution_detail_page: SolutionDetailPageBlock,
  user_groups_page: UserGroupsPageBlock,
  api_docs_page: ApiDocsPageBlock,
  solutions_landing_page: SolutionsPageBlock,
  products_landing_page: ProductsPageBlock,
  exchange_page: ExchangePageBlock,
  elite_supplier_page: EliteSupplierPageBlock,
  integrations_page: IntegrationsPageBlock,
  academy_page: AcademyPageBlock,
  events_page: EventsPageBlock,
  openhouse_page: OpenHousePageBlock,
  customer_portal_page: CustomerPortalPageBlock,
};

/**
 * Renders a single block by dispatching to the correct renderer based on
 * `block.type`. `index` is passed along so blocks like `two_column` can zebra
 * their backgrounds without knowing their absolute position.
 */
export function BlockRenderer({ block, index }) {
  const Comp = RENDERERS[block.type];
  if (!Comp) {
    return (
      <div className="p-4 text-xs text-slate-400" data-testid={`block-unknown-${block.type}`}>
        Unknown block type: {block.type}
      </div>
    );
  }
  return <Comp data={block.data} index={index} />;
}

/**
 * Public page that fetches its content from /api/site-pages/{slug} and renders
 * the ordered `blocks` array. Falls back to a friendly loading state; if the
 * endpoint returns 404 the page renders nothing (React Router shouldn't be
 * pointing at a slug that doesn't exist in the registry).
 */
export default function BlockRendererPage({ slug, testId, jsonLd }) {
  const [page, setPage] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/site-pages/${slug}`);
        if (!cancelled) setPage(data);
      } catch {
        // silent - just don't render
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (!loaded) {
    return <div className="min-h-screen bg-white" data-testid={`${testId || slug}-loading`} />;
  }
  if (!page) return null;

  const blocks = Array.isArray(page.blocks) ? page.blocks : [];
  // Renderers use `index` for zebra striping. We only pass sequential indices
  // to blocks that actually alternate (two_column). Other blocks ignore it.
  let twoColIdx = 0;

  return (
    <div data-testid={testId || `page-${slug}`} className="page-transition">
      {(page.seo_title || page.seo_description || jsonLd) && (
        <Seo
          title={page.seo_title}
          description={page.seo_description}
          keywords={page.seo_keywords}
          path={page.public_path}
          jsonLd={jsonLd}
        />
      )}
      {blocks.map((block) => {
        const idx = block.type === "two_column" ? twoColIdx++ : 0;
        return <BlockRenderer key={block.id} block={block} index={idx} />;
      })}
    </div>
  );
}
